import { STATUS_CLASSES, STATUS_CLASS_CONFIG } from '@/config/httpStatus';
import { formatNumber, formatPercent } from '@/utils/format';
import type { StatusClassCounts } from '@/types/stats';
import './stats.css';

export interface StatusClassBarProps {
  counts: StatusClassCounts;
  /** Accessible name prefix, e.g. "mskse.ru status mix" */
  label: string;
  size?: 'sm' | 'lg';
}

/**
 * Horizontal 100% bar of status-class shares. Segment widths are
 * proportional to the counts; server errors start at the left edge so their
 * share compares across rows. Values are in the accessible label and each
 * segment's tooltip.
 */
export function StatusClassBar({ counts, label, size = 'sm' }: StatusClassBarProps) {
  const total = STATUS_CLASSES.reduce((sum, cls) => sum + counts[cls], 0);
  const present = STATUS_CLASSES.filter((cls) => counts[cls] > 0);

  const describe = (cls: (typeof present)[number]) =>
    `${cls} ${formatPercent(counts[cls] / total)} (${formatNumber(counts[cls])})`;
  const ariaLabel = total > 0 ? `${label}: ${present.map(describe).join(', ')}` : `${label}: no requests`;

  return (
    <div className={`status-bar status-bar-${size}`} role="img" aria-label={ariaLabel}>
      {present.map((cls) => (
        <span
          key={cls}
          className={`status-bar-segment ${STATUS_CLASS_CONFIG[cls].className}`}
          style={{ flexGrow: counts[cls] }}
          data-status-class={cls}
          title={`${STATUS_CLASS_CONFIG[cls].label}: ${formatNumber(counts[cls])} (${formatPercent(counts[cls] / total)})`}
        />
      ))}
    </div>
  );
}
